import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { login } from "@/lib/api";
import { Loader2, ShieldCheck, Zap } from "lucide-react";

export default function LoginPage({ onLogin }) {
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error("Bitte geben Sie das Passwort ein");
      return;
    }

    setIsLoading(true);
    try {
      const response = await login("admin", password);
      toast.success("Erfolgreich angemeldet");
      onLogin({
        token: response.data.access_token,
        username: response.data.username || "admin",
        role: response.data.role || "admin",
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Anmeldung fehlgeschlagen");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" data-testid="login-page">
      {/* Mobile Header - Branding (visible only on mobile) */}
      <div 
        className="lg:hidden relative overflow-hidden py-8 px-6"
        style={{
          background: 'linear-gradient(135deg, #00B4D8 0%, #0077B6 50%, #023E8A 100%)',
        }}
      >
        {/* Decorative circles for mobile */}
        <div className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-10 bg-white" />
        <div className="absolute bottom-0 left-0 w-24 h-24 rounded-full opacity-10 bg-white" />
        
        <div className="relative z-10 flex flex-col items-center text-center">
          {/* Logo */}
          <div className="w-16 h-16 mb-4 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Zap className="w-8 h-8 text-white" />
          </div>
          <p className="text-cyan-100 text-sm font-medium tracking-wider uppercase">
            Deine persönliche
          </p>
          <h2 className="text-3xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Kraftquelle
          </h2>
        </div>
      </div>

      {/* Form Section */}
      <div className="w-full lg:w-[45%] flex items-center justify-center p-6 sm:p-8 bg-white flex-1 lg:flex-none">
        <div className="w-full max-w-md">
          {/* Logo - hidden on mobile (shown in header), visible on desktop */}
          <div className="hidden lg:flex items-center gap-3 mb-12">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #00B4D8 0%, #0077B6 100%)' }}>
              <Zap className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', color: '#0077B6' }}>
                sanza
              </h1>
              <p className="text-sm text-slate-500 font-medium">Serial Form</p>
            </div>
          </div>

          {/* Mobile Logo Text */}
          <div className="lg:hidden flex items-center gap-2 mb-6 mt-2">
            <h1 className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', color: '#0077B6' }}>
              sanza
            </h1>
            <span className="text-sm text-slate-500 font-medium">Serial Form</span>
          </div>

          <Card className="border-0 shadow-none">
            <CardHeader className="px-0 pb-4 lg:pb-6">
              <CardTitle className="text-xl lg:text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Willkommen
              </CardTitle>
              <CardDescription className="text-slate-500 text-sm">
                Produktinformationen & Seriennummern-Verwaltung
              </CardDescription>
            </CardHeader>
            <CardContent className="px-0">
              <form onSubmit={handleSubmit} className="space-y-4 lg:space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                    Passwort
                  </Label>
                  <Input
                    id="password"
                    data-testid="login-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="h-12 border-slate-200 focus:ring-cyan-500 focus:border-cyan-500 rounded-xl"
                    disabled={isLoading}
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="login-submit"
                  className="w-full h-12 text-white font-semibold rounded-xl transition-all duration-300 hover:shadow-lg"
                  style={{ background: 'linear-gradient(135deg, #00B4D8 0%, #0077B6 100%)' }}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Anmeldung läuft...
                    </>
                  ) : (
                    "Anmelden"
                  )}
                </Button>
              </form>

              <div className="mt-6 lg:mt-8 pt-4 lg:pt-6 border-t border-slate-100">
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <ShieldCheck className="w-4 h-4" />
                  <span>Sichere Verbindung</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right Side - Branding (Desktop only) */}
      <div className="hidden lg:block lg:w-[55%] relative overflow-hidden">
        {/* Background with gradient */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, #00B4D8 0%, #0077B6 50%, #023E8A 100%)',
          }}
        />
        
        {/* Decorative circles */}
        <div className="absolute top-20 right-20 w-64 h-64 rounded-full opacity-10 bg-white" />
        <div className="absolute bottom-40 left-10 w-48 h-48 rounded-full opacity-10 bg-white" />
        <div className="absolute top-1/2 right-1/3 w-32 h-32 rounded-full opacity-5 bg-white" />
        
        {/* Content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center p-12 text-center">
          <div className="max-w-lg">
            {/* Logo icon large */}
            <div className="w-24 h-24 mx-auto mb-8 rounded-3xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <Zap className="w-12 h-12 text-white" />
            </div>
            
            <p className="text-cyan-100 text-lg mb-2 font-medium tracking-wider uppercase">
              Deine persönliche
            </p>
            <h2 className="text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Kraftquelle
            </h2>
            
            {/* Product image placeholder */}
            <div className="mt-12">
              <img 
                src="https://www.sanzanet.com/cms_media/my_media/0/22/lp/sanza_generator_611_shadow.png" 
                alt="sanza pro Generator"
                className="max-w-xs mx-auto drop-shadow-2xl"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
